import React, { useState, useEffect } from 'react';
import { Plus, Search, Download, Trash2, Euro, DollarSign, PoundSterling, Coins, Calendar, Tag, FileText, Receipt, Edit2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import AddExpenseModal from '../Modals/AddExpenseModal';
import { convertCurrency } from '../../utils/currency';

interface Expense {
  id: string;
  expense_number: string;
  title: string;
  description: string;
  category: string;
  amount: number;
  currency: string;
  expense_date: string;
  payment_method: string;
  payment_status: string;
  branch_id: string;
  created_by: string;
  receipt_url?: string;
  notes?: string;
  amountInEur?: number;
  branches?: {
    name: string;
  };
}

const Expenses: React.FC = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);

  useEffect(() => {
    fetchExpenses();
  }, [statusFilter, categoryFilter]);

  const fetchExpenses = async () => {
    try {
      console.log('Fetching expenses for user:', user);
      let query = supabase
        .from('expenses')
        .select(`
          *,
          branches (name)
        `)
        .order('expense_date', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('payment_status', statusFilter);
      }

      if (categoryFilter !== 'all') {
        query = query.eq('category', categoryFilter);
      }

      if (user && user.role !== 'admin' && user.role !== 'accounting' && user.unitType !== 'accounting') {
        console.log('Filtering by branch:', user.branchId);
        query = query.eq('branch_id', user.branchId);
      }

      const { data, error } = await query;
      console.log('Expenses query result:', { data, error });
      if (error) throw error;

      if (data) {
        const expensesWithEur = await Promise.all(
          data.map(async (expense) => {
            const amountInEur = await convertCurrency(
              Number(expense.amount),
              expense.currency || 'EUR',
              'EUR'
            );
            return { ...expense, amountInEur };
          })
        );
        setExpenses(expensesWithEur);
      } else {
        setExpenses([]);
      }
    } catch (err) {
      console.error('Error fetching expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpense = async (id: string) => {
    if (!await window.appConfirm('Bu gideri silmek istediğinizden emin misiniz?')) return;

    try {
      const { error } = await supabase.from('expenses').delete().eq('id', id);
      if (error) throw error;
      fetchExpenses();
    } catch (err) {
      console.error('Error deleting expense:', err);
      alert('Gider silinirken hata oluştu');
    }
  };

  const filteredExpenses = expenses.filter(expense =>
    expense.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.expense_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      paid: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
    };
    const labels = {
      pending: 'Bekliyor',
      paid: 'Ödendi',
      cancelled: 'İptal'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[status as keyof typeof badges]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const getCategoryLabel = (category: string) => {
    const categories: { [key: string]: string } = {
      office: 'Ofis',
      salary: 'Maaş',
      marketing: 'Pazarlama',
      utilities: 'Faturalar',
      supplies: 'Malzeme',
      travel: 'Seyahat',
      other: 'Diğer'
    };
    return categories[category] || category;
  };

  const getTotalAmount = () => {
    return filteredExpenses.reduce((sum, expense) => sum + (expense.amountInEur || 0), 0);
  };

  const getPaidAmount = () => {
    return filteredExpenses
      .filter(e => e.payment_status === 'paid')
      .reduce((sum, expense) => sum + (expense.amountInEur || 0), 0);
  };

  const getPendingAmount = () => {
    return filteredExpenses
      .filter(e => e.payment_status === 'pending')
      .reduce((sum, expense) => sum + (expense.amountInEur || 0), 0);
  };

  const getCurrencyIcon = (currency: string, className: string = "w-4 h-4 text-green-600 dark:text-green-400") => {
    switch (currency?.toUpperCase()) {
      case 'EUR': return <Euro className={className} />;
      case 'USD': return <DollarSign className={className} />;
      case 'GBP': return <PoundSterling className={className} />;
      case 'TRY': return <span className="text-xs font-bold text-green-600 dark:text-green-400 leading-none">₺</span>;
      default: return <Coins className={className} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Giderler</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Gider takibi ve yönetimi</p>
        </div>
        {user && (user.role === 'admin' || user.role === 'accounting' || user.unitType === 'accounting') && (
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            <Plus className="w-5 h-5" />
            Yeni Gider
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Euro className="w-5 h-5" />
            </div>
          </div>
          <p className="text-blue-100 text-xs mb-1.5 font-medium">Toplam Gider</p>
          <p className="text-2xl font-bold tracking-tight">€{Math.round(getTotalAmount()).toLocaleString()}</p>
          <p className="text-blue-100 text-xs mt-1.5">{filteredExpenses.length} kayıt</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Receipt className="w-5 h-5" />
            </div>
          </div>
          <p className="text-green-100 text-xs mb-1.5 font-medium">Ödenen</p>
          <p className="text-2xl font-bold tracking-tight">€{Math.round(getPaidAmount()).toLocaleString()}</p>
          <p className="text-green-100 text-xs mt-1.5">
            {filteredExpenses.filter(e => e.payment_status === 'paid').length} ödeme
          </p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
          </div>
          <p className="text-orange-100 text-xs mb-1.5 font-medium">Bekleyen</p>
          <p className="text-2xl font-bold tracking-tight">€{Math.round(getPendingAmount()).toLocaleString()}</p>
          <p className="text-orange-100 text-xs mt-1.5">
            {filteredExpenses.filter(e => e.payment_status === 'pending').length} bekliyor
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Gider ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tüm Kategoriler</option>
          <option value="office">Ofis</option>
          <option value="salary">Maaş</option>
          <option value="marketing">Pazarlama</option>
          <option value="utilities">Faturalar</option>
          <option value="supplies">Malzeme</option>
          <option value="travel">Seyahat</option>
          <option value="other">Diğer</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tüm Durumlar</option>
          <option value="pending">Bekliyor</option>
          <option value="paid">Ödendi</option>
          <option value="cancelled">İptal</option>
        </select>
      </div>

      {filteredExpenses.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-900 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">Gider bulunamadı</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
              ? 'Arama kriterlerinize uygun gider bulunamadı.'
              : 'Henüz gider eklenmemiş.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filteredExpenses.map((expense) => (
            <div
              key={expense.id}
              className="bg-white dark:bg-gray-900 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-5 hover:shadow-xl hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {expense.title}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-1">
                        {expense.expense_number}
                      </p>
                    </div>
                    {getStatusBadge(expense.payment_status)}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center w-8 h-8">
                        {getCurrencyIcon(expense.currency)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Tutar</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {expense.currency} {expense.amount.toLocaleString()}
                        </p>
                        {expense.currency !== 'EUR' && expense.amountInEur && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            ≈ €{Math.round(expense.amountInEur).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Tarih</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {format(new Date(expense.expense_date), 'dd MMM yyyy', { locale: tr })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-violet-50 dark:bg-violet-900/20 rounded-lg">
                        <Tag className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Kategori</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {getCategoryLabel(expense.category)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <FileText className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Şube</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {expense.branches?.name || 'Bilinmiyor'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {expense.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {expense.description}
                    </p>
                  )}

                  {expense.payment_method && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      <span className="font-semibold">Ödeme:</span> {expense.payment_method}
                    </p>
                  )}

                  <div className="flex items-center gap-2">
                    {expense.receipt_url && (
                      <a
                        href={expense.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Fiş/Fatura İndir
                      </a>
                    )}

                    {user && (user.role === 'admin' || user.role === 'accounting' || user.unitType === 'accounting') && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingExpense(expense);
                            setShowAddModal(true);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                          Düzenle
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Sil
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddExpenseModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingExpense(null);
        }}
        onSuccess={() => {
          fetchExpenses();
          setEditingExpense(null);
        }}
        initialData={editingExpense}
      />
    </div>
  );
};

export default Expenses;
